import {
  HtmlStepperField,
  HtmlStepperFieldFlat,
  StepperPreviewEventEditField,
  StepperPreviewEventEditHtmlAction,
  StepperPreviewEventSelectImage,
  StepperPreviewEventSelectThumbnail,
} from '@superfunnel/types';
import { useCallback, useState } from 'react';

/**
 * Unified data for opening the edit field modal.
 * Can come from iframe or popup - elementPath is always derived from field.id.
 */
export interface EditFieldModalState {
  /** The field data to edit */
  field: HtmlStepperFieldFlat;
  /** Step ID (always known) */
  stepId: string;
}

export interface UseHtmlStepperEditorModalsReturn {
  // Page settings modal (DynamicPage settings)
  pageSettingsModalOpen: boolean;
  setPageSettingsModalOpen: (open: boolean) => void;

  // Edit HTML action modal
  editHtmlActionModalData: StepperPreviewEventEditHtmlAction['data'] | null;
  setEditHtmlActionModalData: (
    data: StepperPreviewEventEditHtmlAction['data'] | null,
  ) => void;
  onOpenEditHtmlActionModal: (
    data: StepperPreviewEventEditHtmlAction['data'],
  ) => void;

  // Edit field modal (unified)
  editFieldModalData: EditFieldModalState | null;
  setEditFieldModalData: (data: EditFieldModalState | null) => void;
  /** Open edit field modal from iframe event */
  onOpenEditFieldModal: (data: StepperPreviewEventEditField['data']) => void;
  /** Open edit field modal from popup */
  openEditFieldModal: (stepId: string, field: HtmlStepperField) => void;

  // Select image modal
  selectImageModalData: StepperPreviewEventSelectImage['data'] | null;
  setSelectImageModalData: (
    data: StepperPreviewEventSelectImage['data'] | null,
  ) => void;
  onOpenSelectImageModal: (
    data: StepperPreviewEventSelectImage['data'],
  ) => void;

  // Select thumbnail modal (for video poster images)
  selectThumbnailModalData: StepperPreviewEventSelectThumbnail['data'] | null;
  setSelectThumbnailModalData: (
    data: StepperPreviewEventSelectThumbnail['data'] | null,
  ) => void;
  onOpenSelectThumbnailModal: (
    data: StepperPreviewEventSelectThumbnail['data'],
  ) => void;
}

export function useHtmlStepperEditorModals(): UseHtmlStepperEditorModalsReturn {
  // Page settings modal (DynamicPage settings)
  const [pageSettingsModalOpen, setPageSettingsModalOpen] = useState(false);

  // Edit HTML action modal
  const [editHtmlActionModalData, setEditHtmlActionModalData] = useState<
    StepperPreviewEventEditHtmlAction['data'] | null
  >(null);

  // Edit field modal (unified)
  const [editFieldModalData, setEditFieldModalData] =
    useState<EditFieldModalState | null>(null);

  // Select image modal
  const [selectImageModalData, setSelectImageModalData] = useState<
    StepperPreviewEventSelectImage['data'] | null
  >(null);

  // Select thumbnail modal (for video poster images)
  const [selectThumbnailModalData, setSelectThumbnailModalData] = useState<
    StepperPreviewEventSelectThumbnail['data'] | null
  >(null);

  // Modal open callbacks
  const onOpenEditHtmlActionModal = useCallback(
    (data: StepperPreviewEventEditHtmlAction['data']) => {
      console.log('[HtmlStepperEditor] Open editHtmlAction modal:', data);
      setEditHtmlActionModalData(data);
    },
    [],
  );

  // Open edit field modal from iframe event
  const onOpenEditFieldModal = useCallback(
    (data: StepperPreviewEventEditField['data']) => {
      console.log(
        '[HtmlStepperEditor] Open editField modal from iframe:',
        data,
      );
      setEditFieldModalData({
        field: data.content,
        stepId: data.target.type === 'step' ? data.target.stepId : '',
      });
    },
    [],
  );

  // Open edit field modal from popup
  const openEditFieldModal = useCallback(
    (stepId: string, field: HtmlStepperField) => {
      console.log('[HtmlStepperEditor] Open editField modal from popup:', {
        stepId,
        field,
      });
      setEditFieldModalData({
        field: field as HtmlStepperFieldFlat,
        stepId,
      });
    },
    [],
  );

  const onOpenSelectImageModal = useCallback(
    (data: StepperPreviewEventSelectImage['data']) => {
      console.log('[HtmlStepperEditor] Open selectImage modal:', data);
      setSelectImageModalData(data);
    },
    [],
  );

  const onOpenSelectThumbnailModal = useCallback(
    (data: StepperPreviewEventSelectThumbnail['data']) => {
      console.log('[HtmlStepperEditor] Open selectThumbnail modal:', data);
      setSelectThumbnailModalData(data);
    },
    [],
  );

  return {
    pageSettingsModalOpen,
    setPageSettingsModalOpen,
    editHtmlActionModalData,
    setEditHtmlActionModalData,
    onOpenEditHtmlActionModal,
    editFieldModalData,
    setEditFieldModalData,
    onOpenEditFieldModal,
    openEditFieldModal,
    selectImageModalData,
    setSelectImageModalData,
    onOpenSelectImageModal,
    selectThumbnailModalData,
    setSelectThumbnailModalData,
    onOpenSelectThumbnailModal,
  };
}
